import React, { useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const TILING_CATEGORIES = ["Wet Rooms & Walk-In Showers", "Bathroom Renovation Projects", "Floor Tiling", "Wall Tiling", "45° Mitred Tile Edges", "Large Format Tiles"];
const IMAGE_PLACEHOLDER = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/tiling_project_placeholder.png";
const PORTFOLIO_BANNER = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/luxury_bathroom_portfolio_banner.png";
function PortfolioGallery({
  activeCategory
}: {
  activeCategory: string;
}) {
  const {
    posts,
    loading
  } = usePosts();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  if (loading) {
    return <div className="grid grid-cols-2 gap-3 p-3">
        {[...Array(6)].map((_, i) => <div key={i} className="aspect-square bg-muted-foreground/10 animate-pulse" />)}
      </div>;
  }
  return <div className="flex flex-col">
      <div className="grid grid-cols-2 gap-3 p-3">
        {posts?.map((post, i) => <div key={post.id} data-index={i} className="relative aspect-square overflow-hidden cursor-pointer group bg-background border border-border/50" onClick={() => setSelectedImage(post.featuredImage || IMAGE_PLACEHOLDER)}>
            <img src={post.featuredImage || IMAGE_PLACEHOLDER} alt={`${post.title} - ${activeCategory}`} loading="lazy" className="max-w-full h-auto w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <span className="text-foreground text-[10px] uppercase tracking-widest font-sans border border-foreground/20 px-3 py-1 bg-black/20 backdrop-blur-sm">
                View Project
              </span>
            </div>
          </div>)}
      </div>

      <div className="p-4 bg-card border-t border-border">
        <Button asChild variant="outline" className="w-full border-muted-foreground/20 text-muted-foreground hover:border-[var(--ring)] hover:text-foreground uppercase text-[10px] tracking-widest h-11">
          <Link to="/ProjectGalleryPage">
            View Full Portfolio
          </Link>
        </Button>
        <img src={PORTFOLIO_BANNER} alt="Luxury tiling project showcase banner" loading="lazy" className="w-full mt-2 h-auto object-cover" />
      </div>

      {selectedImage && <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-md flex items-center justify-center p-4 md:p-12 cursor-zoom-out" onClick={() => setSelectedImage(null)} role="dialog" aria-modal="true">
          <Button variant="ghost" size="icon" className="absolute top-6 right-6 text-foreground hover:bg-muted z-10" onClick={e => {
        e.stopPropagation();
        setSelectedImage(null);
      }}>
            <X className="w-6 h-6" />
          </Button>
          <img src={selectedImage} alt="Portfolio Highlight" loading="lazy" className="max-w-full max-h-full object-contain shadow-2xl border border-border" />
        </div>}
    </div>;
}
export default function TheLondonTilingStandard() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const [activeCategory, setActiveCategory] = useState(TILING_CATEGORIES[0]);
  const wp_query_params = {
    per_page: 8,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="the-london-tiling-standard" className="py-[120px] overflow-hidden !bg-[hsl(240,6%,90%)]" ref={ref}>
      <style dangerouslySetInnerHTML={{
      __html: `
        :root {
          --background: hsl(0 0% 100%);
          --foreground: hsl(0 0% 0%);
          --card-foreground: hsl(0 0% 0%);
          --popover-foreground: hsl(0 0% 0%);
          --primary-foreground: hsl(0 0% 0%);
          --secondary-foreground: hsl(0 0% 0%);
          --muted-foreground: hsl(0 0% 0%);
          --sidebar-foreground: hsl(0 0% 0%);
          --accent-foreground: hsl(0 0% 0%);
        }
        .dark {
          --foreground: hsl(0 0% 0%);
          --card-foreground: hsl(0 0% 0%);
          --popover-foreground: hsl(0 0% 0%);
          --primary-foreground: hsl(0 0% 0%);
          --secondary-foreground: hsl(0 0% 0%);
          --muted-foreground: hsl(0 0% 0%);
          --sidebar-foreground: hsl(0 0% 0%);
          --accent-foreground: hsl(0 0% 0%);
        }
      `
    }} />
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className={`lg:col-span-7 relative transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="flex flex-wrap gap-2 mb-8 overflow-x-auto pb-2 scrollbar-hide no-scrollbar">
              {TILING_CATEGORIES.map((cat, i) => <Button key={cat} variant={activeCategory === cat ? "default" : "outline"} size="sm" onClick={() => setActiveCategory(cat)} data-index={i} className={`text-[10px] uppercase tracking-widest transition-all duration-300 ${activeCategory === cat ? "bg-[var(--ring)] text-foreground" : "border-muted-foreground/20 text-muted-foreground hover:border-[var(--ring)]"}`}>
                  {cat}
                </Button>)}
            </div>

            <div className="relative">
              <div className="absolute inset-0 border border-[var(--ring)] translate-x-4 translate-y-4 z-0 hidden md:block" />
              
              <div className="relative z-10 overflow-hidden bg-muted shadow-2xl">
                <PostsProvider wp_query={{
                per_page: 8,
                orderby: "date",
                order: "desc"
              }}>
                  <PortfolioGallery activeCategory={activeCategory} />
                </PostsProvider>
              </div>
              
              
            </div>
          </div>

          <div className={`lg:col-span-5 flex flex-col justify-center pt-16 lg:pt-24 transition-all duration-1000 delay-300 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="mb-8">
              <span className="block text-foreground font-bold tracking-widest uppercase text-sm mb-2">
                Est. 2013
              </span>
              <h2 className="text-8xl font-serif text-foreground leading-none mb-2">
                11
              </h2>
              <span className="text-3xl font-serif text-muted-foreground italic block mb-8">
                Years of Excellence
              </span>
              
              <h3 className="text-2xl font-sans font-semibold text-foreground mb-6">
                Precision Craftsmanship in London
              </h3>
              
              <p className="text-muted-foreground text-lg leading-relaxed mb-6 font-sans">
                At SimProTiling, we don't just lay tiles; we engineer surfaces. 
                Our approach combines traditional British craftsmanship with modern 
                technical precision. Every cut is calculated, every line is laser-leveled, 
                and every finish is scrutinized to meet the highest standards of luxury interiors.
              </p>

              <p className="text-muted-foreground text-lg leading-relaxed mb-8 font-sans">
                Specialising in large format porcelain and intricate natural stone, 
                our team brings over a decade of experience to London's most prestigious 
                homes, ensuring that your renovation is not only beautiful but built to last.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-primary text-white hover:bg-primary/90">
                  <Link to="/ProjectGalleryPage">
                    View Our Portfolio <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-input hover:bg-accent hover:text-accent-foreground">
                  <Link to="/ExpertServicesPage">
                    Explore Services
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}